import { LocalDate } from "@js-joda/core"
import { ClinicalProtocolType } from "./ClinicalProtocol"
import {
  DeviceOrder,
  DeviceOrderDto,
  DeviceType,
  toDeviceOrder,
} from "./DeviceOrders"
import { SupportalCustomerSupportNotes } from "./Patient"

export type Section = "population" | "support"

export type JourneyStatus =
  | "cs-flags"
  | "enrollment"
  | "order-devices"
  | "shipping"
  | "troubleshooting"

export type TrackingStatus =
  | "pending"
  | "sent"
  | "pending-delivery"
  | "delivered"
  | "failed"

export type ErrorType = "device-not-suspended" | "duplicate-active-devices"

export type EnrollmentStatus = "STARTED" | "READY_TO_ORDER_DEVICE" | "COMPLETED"

export type ClinicalProgram = "HTN" | "DM"

export type CustomerSupportPatientWithPrescriptionData = {
  patient: CustomerSupportPatient
  organizationName: string
  prescriptionTypes: ClinicalProtocolType[]
}

export type DeviceOrderWithPrescription = {
  deviceOrder: DeviceOrder
  prescription: CustomerSupportPrescription
}

export type CustomerSupportDeviceOrderDto = {
  organizationName: string
  patient: CustomerSupportPatient
  deviceOrder: DeviceOrderDto
}

export type CustomerSupportDeviceOrder = {
  organizationName: string
  patient: CustomerSupportPatient
  deviceOrder: DeviceOrder
}

export const toCustomerSupportDeviceOrder = (
  dto: CustomerSupportDeviceOrderDto
): CustomerSupportDeviceOrder => ({
  ...dto,
  deviceOrder: toDeviceOrder(dto.deviceOrder),
})

export type CustomerSupportTroubleshootingItem = {
  prescriptionId: string
  organizationName: string
  prescriptionType: ClinicalProtocolType
  patient: CustomerSupportPatient
  error: ErrorType
}

export type CustomerSupportPatient = {
  id: string
  bloomlifeId: string
  firstName: string
  lastName: string
  medicalRecordNumber?: string
  phoneNumber?: string
  shippingAddress?: {
    address1?: string
    address2?: string
    city?: string
    state?: string
    zipCode?: string
  }
  hasBloodPressureMonitor: boolean
  hasBloodGlucoseMonitor: boolean
  customerSupportNotes?: SupportalCustomerSupportNotes
  isAttCompatible?: boolean
}

export const getPatientFullName = (
  patient: Pick<CustomerSupportPatient, "firstName" | "lastName">
): string => {
  return `${patient.lastName}, ${patient.firstName}`
}

export type CustomerSupportCaregiver = {
  id: string
  caregiverDisplayName: string
  caregiverFirstName: string
  caregiverLastName: string
}

export type CustomerSupportPrescriptionDto = {
  id: string
  organizationName: string
  type: ClinicalProtocolType
  patient: CustomerSupportPatient
  caregiver?: CustomerSupportCaregiver
  startDate: string
  enrollmentStatus: EnrollmentStatus
  deviceOrders: DeviceOrderDto[]
}

export type CustomerSupportPrescription = {
  id: string
  organizationName: string
  type: ClinicalProtocolType
  patient: CustomerSupportPatient
  caregiver?: CustomerSupportCaregiver
  startDate: LocalDate
  enrollmentStatus: EnrollmentStatus
  deviceOrders: DeviceOrder[]
}

export const toCustomerSupportPrescription = (
  dto: CustomerSupportPrescriptionDto
): CustomerSupportPrescription => ({
  ...dto,
  startDate: LocalDate.parse(dto.startDate),
  deviceOrders: dto.deviceOrders.map(toDeviceOrder),
})

export const getPrescriptionProgram = (type: ClinicalProtocolType): string => {
  if (type === "diabetes") {
    return "DM"
  } else if (type === "hypertension") {
    return "HTN"
  }
  throw Error(`Unsupported prescription type [${type}]`)
}

export const getDeviceOrderProgram = (type: DeviceType): string => {
  if (type === "BLOOD_GLUCOSE_MONITOR") {
    return "DM"
  } else if (type === "BLOOD_PRESSURE_MONITOR") {
    return "HTN"
  }
  throw Error(`Unsupported device type [${type}]`)
}

export type CustomerSupportOverviewDto = {
  flaggedPrescriptions: CustomerSupportPrescriptionDto[]
  enrolledPrescriptions: CustomerSupportPrescriptionDto[]
  waitingDeviceOrderPrescriptions: CustomerSupportPrescriptionDto[]
  shippingDeviceOrders: CustomerSupportDeviceOrderDto[]
  troubleshootingItems: CustomerSupportTroubleshootingItem[]
}

export type CustomerSupportOverview = {
  flaggedPrescriptions: CustomerSupportPrescription[]
  enrolledPrescriptions: CustomerSupportPrescription[]
  waitingDeviceOrderPrescriptions: CustomerSupportPrescription[]
  shippingDeviceOrders: CustomerSupportDeviceOrder[]
  troubleshootingItems: CustomerSupportTroubleshootingItem[]
}

export const toCustomerSupportOverview = (
  dto: CustomerSupportOverviewDto
): CustomerSupportOverview => ({
  flaggedPrescriptions: dto.flaggedPrescriptions.map(
    toCustomerSupportPrescription
  ),
  enrolledPrescriptions: dto.enrolledPrescriptions.map(
    toCustomerSupportPrescription
  ),
  waitingDeviceOrderPrescriptions: dto.waitingDeviceOrderPrescriptions.map(
    toCustomerSupportPrescription
  ),
  shippingDeviceOrders: dto.shippingDeviceOrders.map(
    toCustomerSupportDeviceOrder
  ),
  troubleshootingItems: dto.troubleshootingItems,
})

export function buildPatientWithPrescriptionData(
  prescriptions: CustomerSupportPrescription[]
): CustomerSupportPatientWithPrescriptionData[] {
  const map = new Map<string, CustomerSupportPatientWithPrescriptionData>()

  for (const prescription of prescriptions) {
    const patientId = prescription.patient.id

    if (!map.has(patientId)) {
      map.set(patientId, {
        patient: prescription.patient,
        organizationName: prescription.organizationName,
        prescriptionTypes: [prescription.type],
      })
    } else {
      const existing = map.get(patientId)!
      if (!existing.prescriptionTypes.includes(prescription.type)) {
        existing.prescriptionTypes.push(prescription.type)
      }
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const orgCompare = a.organizationName.localeCompare(
      b.organizationName,
      undefined,
      { sensitivity: "base" }
    )
    if (orgCompare !== 0) return orgCompare

    const firstNameCompare = a.patient.firstName.localeCompare(
      b.patient.firstName
    )
    if (firstNameCompare !== 0) return firstNameCompare

    return a.patient.lastName.localeCompare(b.patient.lastName)
  })
}

export function buildShippingDeviceOrder(
  deviceOrders: CustomerSupportDeviceOrder[]
): CustomerSupportDeviceOrder[] {
  return deviceOrders.sort((a, b) => {
    const orgCompare = a.organizationName.localeCompare(
      b.organizationName,
      undefined,
      { sensitivity: "base" }
    )
    if (orgCompare !== 0) return orgCompare

    const firstNameCompare = a.patient.firstName.localeCompare(
      b.patient.firstName
    )
    if (firstNameCompare !== 0) return firstNameCompare

    return a.patient.lastName.localeCompare(b.patient.lastName)
  })
}
